import { isEmpty, merge } from 'lodash';

import store from 'a/store';
import { aquaAxiosWithCreds } from 'root/lib/utilityFunctions';
import { cancelDebouncedSave, debouncedSaveGameState, saveGameState } from 'a/actions/persistStateActions';
import { getErrorActionObject, showStandardErrorPage } from 'a/components/GlobalErrorHandler/actions';
import { getIsDeepLink } from 'a/hub/selectors';

import { AuthData } from './authorize';
import ReaderAPI from '../readerAPI';
import { isAnsweredCorrectlyOnFirstTry } from '../activities';
import { GAME_STATE_URL } from '../../data/environment';
import { updateLocalBookState } from '../../redux/actions/game_details';

/**
 * Merges book's current gamestate with new data
 * @param {object} bookState book's current gamestate
 * @param {object} newData new data to merge into existing state
 */
const mergeBookState = (bookState, newData) => {
  let gameDetails = { ...bookState };

  // Handle reset activity answers operation separately (handled differently than other operations)
  if (newData.isMasteryRetry !== undefined) {
    const { isMasteryRetry } = newData;
    const filterCorrectAnswers = answer => isMasteryRetry && isAnsweredCorrectlyOnFirstTry(answer);

    // Apply filtering to all answer arrays
    gameDetails.activityAnswers = (gameDetails.activityAnswers || [])
      .filter(answer => isAnsweredCorrectlyOnFirstTry({ ...answer, attemptsBase: 0 }));
    gameDetails.evidenceTaggingAnswers = (gameDetails.evidenceTaggingAnswers || []).filter(filterCorrectAnswers);
    gameDetails.postReadingAnswers = (gameDetails.postReadingAnswers || []).filter(filterCorrectAnswers);
    gameDetails.graphicOrganizerAnswers = (gameDetails.graphicOrganizerAnswers || []).filter(filterCorrectAnswers);
    return gameDetails;
  }

  // Default merge behavior for all other operations
  const {
    activityAnswers, postReadingAnswers, graphicOrganizerAnswers,
    evidenceTaggingAnswers, questOverride, responseData, showAlerts,
  } = newData;

  if (!isEmpty(questOverride)) {
    gameDetails.questOverride = merge(gameDetails.questOverride, questOverride);
  } else if (!isEmpty(activityAnswers)) {
    gameDetails.activityAnswers = (gameDetails.activityAnswers || []).concat(activityAnswers);
  } else if (!isEmpty(postReadingAnswers)) {
    gameDetails.postReadingAnswers = (gameDetails.postReadingAnswers || []).concat(postReadingAnswers);
  } else if (!isEmpty(graphicOrganizerAnswers)) {
    gameDetails.graphicOrganizerAnswers = (gameDetails.graphicOrganizerAnswers || []).concat(graphicOrganizerAnswers);
  } else if (!isEmpty(evidenceTaggingAnswers)) {
    gameDetails.evidenceTaggingAnswers = (gameDetails.evidenceTaggingAnswers || []).concat(evidenceTaggingAnswers);
  } else {
    gameDetails = { ...gameDetails, ...newData };
  }

  if (!isEmpty(responseData)) {
    gameDetails.accurateResponseData = { ...responseData };
  }

  if (showAlerts !== undefined) {
    gameDetails.showAlerts = showAlerts;
  }

  return gameDetails;
};

/**
 * Gets current eReader book's gamestate from redux (should match actual gamestate)
 * @param {string} bookId current eReader book id
 */
export const getLocalBookState = (bookId) => {
  const { eReader: { gameDetailsReducer } } = store.getState();
  if (gameDetailsReducer?.bookId === bookId && gameDetailsReducer?.gameDetails) {
    return gameDetailsReducer.gameDetails;
  }

  return {};
};

/**
 * Sends GET to current eReader book's gamestate
 * Resolves immediately if deep link
 * @param {string} bookId current eReader book id
 * @returns {Promise} resolves if deep link or with book's gamestate data
 */
export const getBookState = (bookId) => {
  const isDeepLink = getIsDeepLink(store.getState());
  const userId = AuthData.studentId;
  const gameId = `ereader.${bookId}`;
  const requestUrl = `${GAME_STATE_URL}?gameId=${gameId}&userId=${userId}`;

  if (isDeepLink) {
    return Promise.resolve({});
  }

  return aquaAxiosWithCreds(requestUrl)
    .then(({ data }) => data[gameId]?.GameData?.gameDetails ?? {})
    .catch((error) => {
      // Fail loudly if no gamestate found to prevent progress loss
      store.dispatch(getErrorActionObject(gameId, error.toString(), 'API: eReader', 'GET eReader gamestate', 'api'));
      store.dispatch(showStandardErrorPage('BOOK_STATE_FETCH_ERROR', 'Failed to load book progress'));
      throw error;
    });
};

/**
 * Merges book's current gamestate (stored in redux) with incoming data and persists it.
 * Two modes, selected by `debounceMs`:
 *  - number (default 250): updates redux immediately and queues a debounced POST; the
 *    returned promise resolves right away (the POST happens asynchronously, fire-and-forget).
 *  - null: persists immediately and awaited — cancels any pending debounced save for this
 *    book (so it can't overwrite this one), POSTs now, and updates redux on success; the
 *    returned promise resolves only after the POST succeeds and rejects if it fails. Use
 *    when the caller must know the save landed before proceeding (e.g. marking a book complete).
 * No-op for deep links (returns a resolved promise without saving).
 * @param {string} bookId current eReader book id
 * @param {object} data new data to be merged into book's gamestate
 * @param {?number} [debounceMs=250] debounce in ms for the queued POST, or `null` for an immediate awaited save
 * @returns {Promise<void>} resolves once the save is queued (debounced mode) or committed (immediate mode)
 */
export const saveBookState = (bookId, data, debounceMs = 250) => {
  // Don't save gamestate for deep links
  const isDeepLink = getIsDeepLink(store.getState());
  if (isDeepLink) {
    return Promise.resolve();
  }

  // Get book's gamestate from redux and merge data
  const gameId = `ereader.${bookId}`;
  const currentBookState = getLocalBookState(bookId);
  const gameDetails = mergeBookState(currentBookState, data);

  const gameState = {
    gameId,
    gameData: { gameDetails },
  };

  if (debounceMs === null) {
    // Drop any older queued/in-flight debounced save for this book so it can't
    // land after this immediate save and overwrite the fresher (e.g. complete) state.
    cancelDebouncedSave(gameId);
    return store.dispatch(saveGameState(gameId, { gameDetails })).then(() => {
      store.dispatch(updateLocalBookState(gameDetails));
    });
  }
  // Update redux and debounce gamestate POST request
  store.dispatch(updateLocalBookState(gameDetails));
  store.dispatch(debouncedSaveGameState(gameState, { debounceMs }));
  return Promise.resolve();
};

function toQuestData(data, { chapterId, isPractice } = {}) {
  if (chapterId || isPractice) {
    const resultData = {};
    if (isPractice) {
      resultData.practice = data;
    } else if (chapterId) {
      resultData[chapterId] = data;
    }
    return { questOverride: resultData };
  }

  return data;
}

export const setRoundSessionId = (bookId, questDescriptor, roundSessionId) => {
  const saveData = toQuestData({ roundSessionId }, questDescriptor);
  saveBookState(bookId, saveData);
};

export function setTitleChapterId(bookId, titleChapterId) {
  saveBookState(bookId, { titleChapterId });
}

export function setCurrentPage(bookId, questDescriptor, currentChapterId, page) {
  const saveData = toQuestData({ currentChapterId, currentPage: page }, questDescriptor);
  saveBookState(bookId, saveData, 1000); // 1000ms debounce to avoid spam click POST requests
}

export function setReadingMode(bookId, mode) {
  saveBookState(bookId, { readingMode: mode });
}

export const setAccurateResponseData = (bookId, responseData) => {
  saveBookState(bookId, { responseData });
};

export function setActivityAnswer(activityId, correct, attempts, selectedAnswerId, bookAttempts, responseData) {
  const bookId = ReaderAPI.getBookId();
  saveBookState(bookId, {
    activityAnswers: [{ activityId, correct, attempts, selectedAnswerId, bookAttempts }],
    responseData,
  });
}

export function setGraphicOrganizerAnswer(graphicOrganizerId, correct, attempts, bookAttempts, responseData) {
  const bookId = ReaderAPI.getBookId();
  saveBookState(bookId, {
    graphicOrganizerAnswers: [{ graphicOrganizerId, correct, attempts, bookAttempts }],
    responseData,
  });
}

export function setPostReadingAnswer(questionId, correct, attempts, bookAttempts, responseData) {
  const bookId = ReaderAPI.getBookId();
  saveBookState(bookId, {
    postReadingAnswers: [{ questionId, correct, attempts, bookAttempts }],
    responseData,
  });
}

export function setEvidenceTaggingAnswer(activityId, correct, attempts, bookAttempts, responseData) {
  const bookId = ReaderAPI.getBookId();
  saveBookState(bookId, {
    evidenceTaggingAnswers: [{ activityId, correct, attempts, bookAttempts }],
    responseData,
  });
}

export function resetActivityAnswers(bookId, isMasteryRetry) {
  saveBookState(bookId, { isMasteryRetry });
}

export function setBookAttempts(bookId, questDescriptor, attempts) {
  const saveData = toQuestData({ attempts }, questDescriptor);
  saveBookState(bookId, saveData);
}

export function setBookStatus(bookId, questDescriptor, status, debounceMs) {
  const saveData = toQuestData({ status }, questDescriptor);
  return saveBookState(bookId, saveData, debounceMs);
}

export function setShowAlerts(bookId, showAlerts) {
  saveBookState(bookId, { showAlerts });
}
